import { IEdoQuestionChoice } from "@/shared/model/edo-question-choice.model";
import { IEdoQuestion } from "@/shared/model/edo-question.model";
import { EdoQuestionTypeEnum } from "@/shared/model/enumerations/edo-question-type.mode";
import { getQuestionType } from "@/shared/util/question-utils";
import { Button, Dialog, DialogContent } from "@/components/ui";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Translate, translate } from "react-jhipster";
import { cn } from "@/shared/util/cn";

interface ViewQuestionProps {
  question: IEdoQuestion;
  open: boolean;
  onClose: () => void;
}

const ViewQuestion = (props: ViewQuestionProps) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [choices, setChoices] = useState<IEdoQuestionChoice[]>([]);

  const { question, open, onClose: handleClose } = props;

  useEffect(() => {
    if (question?.id) {
      setLoading(false);
    }
    if (
      question?.type === EdoQuestionTypeEnum.SINGLE_CHOICE ||
      question?.type === EdoQuestionTypeEnum.TRUE_FALSE ||
      question?.type === EdoQuestionTypeEnum.MULTIPLE_CHOICE ||
      question?.type === EdoQuestionTypeEnum.FILL_BLANK
    ) {
      handleGetChoices();
    }
  }, [question?.id]);

  const handleGetChoices = async () => {
    const response = await axios.get<{ data: IEdoQuestionChoice[] }>(
      `/questions/${question?.id}/choices`
    );
    setChoices(response.data.data);
  };

  const getMultipleChoiceOptions = () => {
    if (!choices?.length) {
      return (
        <div className="p-4 text-center">
          <p className="text-body text-[var(--color-mockup-text-mid)]">
            <Translate contentKey="edocendoApp.edoQuestionChoice.home.notFound">
              No Edo Question Choices found
            </Translate>
          </p>
        </div>
      );
    }

    if (
      question?.type === EdoQuestionTypeEnum.SINGLE_CHOICE ||
      question?.type === EdoQuestionTypeEnum.TRUE_FALSE
    ) {
      return (
        <fieldset className="space-y-2" disabled>
          <legend className="mb-1 text-body font-medium text-[var(--color-mockup-text-mid)]">
            Risposte disponibili
          </legend>
          <div className="flex flex-col gap-2">
            {choices.map((choice) => (
              <label
                key={choice.id}
                className={cn(
                  "flex cursor-default items-center gap-3 rounded-2xl border p-3 transition-colors",
                  choice.isCorrect
                    ? "border-green-300 bg-green-50 dark:border-green-800 dark:bg-green-950/30"
                    : "border-[var(--color-mockup-border)] bg-[var(--color-surface-secondary)]/50 dark:border-[var(--color-mockup-border)] dark:bg-[var(--color-surface-secondary)]/30"
                )}
              >
                <input
                  type="radio"
                  name="view-choices"
                  checked={choice.isCorrect}
                  readOnly
                  className="h-4 w-4 border-[var(--color-mockup-border)] text-primary"
                />
                <span className="text-body text-[var(--color-mockup-text)]">
                  {choice.description}
                </span>
              </label>
            ))}
          </div>
        </fieldset>
      );
    }

    return (
      <fieldset className="mt-4 space-y-2" disabled>
        <legend className="mb-1 text-body font-medium text-[var(--color-mockup-text-mid)]">
          Risposte disponibili
        </legend>
        <div className="flex flex-col gap-2">
          {choices.map((choice) => (
            <label
              key={choice.id}
              className={cn(
                "flex cursor-default items-center gap-3 rounded-2xl border p-3 transition-colors",
                choice.isCorrect
                  ? "border-green-300 bg-green-50 dark:border-green-800 dark:bg-green-950/30"
                  : "border-[var(--color-mockup-border)] bg-[var(--color-surface-secondary)]/50 dark:border-[var(--color-mockup-border)] dark:bg-[var(--color-surface-secondary)]/30"
              )}
            >
              <input
                type="checkbox"
                checked={choice.isCorrect}
                readOnly
                className="h-4 w-4 rounded-sm border-[var(--color-mockup-border)] text-primary"
              />
              <span className="text-body text-[var(--color-mockup-text)]">
                {choice.description}
              </span>
            </label>
          ))}
        </div>
      </fieldset>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <>
            <div className="space-y-4 p-1">
              <div className="flex flex-col gap-1">
                <p className="text-body text-[var(--color-mockup-text)]">
                  {question?.description}
                </p>
                <p className="text-body-sm text-[var(--color-mockup-text-mid)]">
                  {getQuestionType(question?.type)}
                </p>
              </div>

              <div className="flex-1">
                {question?.type === EdoQuestionTypeEnum.FREE_TEXT && (
                  <div className="mb-2">
                    <textarea
                      rows={4}
                      disabled
                      placeholder={translate("edocendoApp.edoQuestion.description")}
                      className="w-full rounded-2xl border border-[var(--color-mockup-border)] bg-[var(--color-surface-secondary)] px-3 py-2 text-body text-[var(--color-mockup-text-mid)] dark:border-[var(--color-mockup-border)] dark:bg-[var(--color-surface-secondary)] dark:text-[var(--color-mockup-text-light)]"
                    />
                  </div>
                )}
                {question?.explanationRequired && (
                  <div className="mb-2">
                    <textarea
                      rows={4}
                      disabled
                      placeholder={translate("edocendoApp.edoQuestion.description")}
                      className="w-full rounded-2xl border border-[var(--color-mockup-border)] bg-[var(--color-surface-secondary)] px-3 py-2 text-body text-[var(--color-mockup-text-mid)] dark:border-[var(--color-mockup-border)] dark:bg-[var(--color-surface-secondary)] dark:text-[var(--color-mockup-text-light)]"
                    />
                  </div>
                )}
                {(question?.type === EdoQuestionTypeEnum.SINGLE_CHOICE ||
                  question?.type === EdoQuestionTypeEnum.TRUE_FALSE ||
                  question?.type === EdoQuestionTypeEnum.MULTIPLE_CHOICE ||
                  question?.type === EdoQuestionTypeEnum.FILL_BLANK) && (
                  <div className="mt-4">{getMultipleChoiceOptions()}</div>
                )}
              </div>
            </div>
            <div className="flex justify-end border-t border-[var(--color-mockup-border)] pt-4 dark:border-[var(--color-mockup-border)]">
              <Button type="button" variant="primary" onClick={handleClose}>
                Chiudi
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ViewQuestion;
